/**
 * Wines Module
 *
 * This module handles wine-related functionality on the site
 * including wine sliders, filters, and interactive elements.
 */

import { gsap } from 'gsap';
import { Power2 } from 'gsap';
import ScrollMagic from 'scrollmagic';
import { TimelineMax } from 'gsap';
import jQuery from 'jquery';
import 'jquery-ui/ui/widgets/slider'; // Import just the slider widget from jQuery UI
import 'slick-carousel'; // Import slick carousel
import 'jquery-nice-select'; // Import jQuery Nice Select
import Barba from '@barba/core';
import App from './App';

const Wines = {
  wineController: null,
  vipIframe: null,

  WineSliderOffering: function () {
    // Check if element exists before initializing
    if (!jQuery('.wine-offering').length) {
      console.log('No wine offerings found, skipping WineSliderOffering');
      return;
    }

    console.log('Initializing WineSliderOffering');

    //Active Toggle
    jQuery('.wine-offering').on('click', function () {
      if (!jQuery(this).hasClass('active')) {
        jQuery('.wine-offering').removeClass('active');
        jQuery(this).addClass('active');
      }
    });

    //Active Slider Change
    jQuery('.wine-offering').on('click', function () {
      function removeSliderClass() {
        jQuery('.wines-slider__container .wine-cart__container .wine-content').removeClass(
          'active'
        );
      }

      // Extract the class that isn't 'wine-offering' or 'active'
      let activeClass = '';
      jQuery(this)
        .attr('class')
        .split(' ')
        .forEach(function (cls) {
          if (cls !== 'wine-offering' && cls !== 'active') {
            activeClass = cls;
          }
        });

      if (activeClass) {
        removeSliderClass();

        // Find the content element with this class
        const $activeContent = jQuery(
          `.wines-slider__container .wine-cart__container .wine-content.${activeClass}`
        );

        $activeContent.addClass('active');

        // Check if content contains Commerce7 products
        const hasC7 = $activeContent.find('.c7-product-collection').length > 0;

        // Let Commerce7.js handle C7 sliders
        if (!hasC7) {
          // Only handle legacy sliders if no C7 content present
          const $slider = $activeContent.find('.slider-content');
          if ($slider.length && $slider.hasClass('slick-initialized')) {
            try {
              $slider.slick('slickGoTo', 0, true);
              console.log(`Reset legacy slider for ${activeClass}`);
            } catch (e) {
              console.error(`Error resetting legacy slider for ${activeClass}:`, e);
            }
          }
        } else {
          console.log(
            `C7 content found in ${activeClass}, letting Commerce7.js handle initialization`
          );

          // For C7 sliders, we need to manually initialize our progress bar
          // Wait a moment for the C7 slider to be fully initialized
          setTimeout(() => {
            const $c7Slider = $activeContent.find(
              '.c7-product-collection__product-list.slick-initialized'
            );
            if ($c7Slider.length) {
              const slickInstance = $c7Slider.slick('getSlick');
              if (slickInstance) {
                // Get the total number of real slides (excluding clones)
                const totalSlides = slickInstance.slideCount;

                // Reset to first slide
                $c7Slider.slick('slickGoTo', 0, true);

                // Calculate segment width for the progress bar handle
                const segmentWidth = (100 / totalSlides).toFixed(4);

                // Update the UI slider handle dimensions
                $activeContent.find('.ui-slider-handle').css({
                  width: segmentWidth + '%',
                  'margin-left': '-' + segmentWidth + '%',
                });

                // Animate to the first position
                gsap.fromTo(
                  $activeContent.find('.ui-slider-handle'),
                  { left: '0%' },
                  {
                    duration: 0.5,
                    ease: 'power2.out',
                    left: segmentWidth + '%',
                  }
                );

                console.log(
                  `Initialized C7 slider progress for ${activeClass}: ${totalSlides} slides`
                );
              }
            }

            // Call the setupC7SliderEvents to ensure event handlers are connected
            if (typeof Wines.setupC7SliderEvents === 'function') {
              Wines.setupC7SliderEvents();
            }
          }, 300);
        }
      }
    });
  },

  WinesACFSlider: function () {
    // Check if element exists before initializing
    if (!jQuery('.wine-cart__container').length) {
      console.log('No wine cart container found, skipping WinesACFSlider');
      return;
    }

    console.log('Initializing WinesACFSlider');

    let $wineCurrentStatus = jQuery('.wine-cart__controller .current-slider'),
      $wineTotalStatus = jQuery('.wine-cart__controller .total-slider'),
      $wineSliderControler,
      $wineSlider = jQuery('.wine-collection-slider'),
      slider,
      $wineTotalCount,
      $wineSlideScrollCount,
      $wineCurrentCount;

    // New approach: Find all product lists and store them in a map with their tab classes
    let sliderMap = {};

    // Handle both legacy class names and dynamic ones
    jQuery('.wine-cart__container .wine-content').each(function () {
      const $content = jQuery(this);
      let contentClasses = $content.attr('class').split(' ');
      let tabClass = '';

      // Find the first class that isn't 'wine-content' or 'active'
      contentClasses.forEach(function (cls) {
        if (cls !== 'wine-content' && cls !== 'active') {
          tabClass = cls;
        }
      });

      if (tabClass) {
        // IMPORTANT: We're now SKIPPING c7 lists entirely - Commerce7.js will handle these
        // Look only for legacy slider content, not C7 lists
        const $slider = $content.find('.slider-content:not(:has(.c7-product-collection))');
        if ($slider.length) {
          sliderMap[tabClass] = $slider;
          console.log('Found legacy slider for tab:', tabClass);
        }
      }
    });

    // Initialize all UI sliders dynamically
    jQuery('.wine-content').each(function () {
      const $content = jQuery(this);
      let contentClasses = $content.attr('class').split(' ');
      let tabClass = '';

      // Find the first class that isn't 'wine-content' or 'active'
      contentClasses.forEach(function (cls) {
        if (cls !== 'wine-content' && cls !== 'active') {
          tabClass = cls;
        }
      });

      if (tabClass) {
        const $progressBar = $content.find('.progress-bar');
        if ($progressBar.length) {
          try {
            // Initialize the jQuery UI slider for the progress bar
            $progressBar.slider({
              min: 0,
              max: 100,
              value: 0,
              animate: 'slow',
              step: 0.001,
              disabled: true, // Disabled for user interaction, just visual
            });

            console.log(`Initialized progress bar for ${tabClass}`);
          } catch (e) {
            console.error(`Error initializing slider for ${tabClass}:`, e);
          }
        }
      }
    });

    // Check if we're on mobile
    const isMobile = window.innerWidth < 992;

    if (isMobile) {
      console.log('Initializing mobile-specific wine slider features');

      // Initialize Nice Select for mobile
      try {
        if (jQuery('#wineOfferings-control').length && jQuery.fn.niceSelect) {
          if (!jQuery('#wineOfferings-control').next('.nice-select').length) {
            jQuery('#wineOfferings-control').niceSelect();
            console.log('Wine offerings niceSelect initialized for mobile');
          }
        }
      } catch (e) {
        console.error('Error initializing mobile niceSelect:', e);
      }

      // Move slider container for mobile layout
      let sliderDetach = jQuery('.wine-cart__container').detach();
      jQuery('.wine-info__container').append(sliderDetach);

      // Initialize mobile-specific slick sliders
      Object.keys(sliderMap).forEach(function (tabClass) {
        const $slider = sliderMap[tabClass];
        if (!$slider.find('.c7-product-collection').length) {
          try {
            if (!$slider.hasClass('slick-initialized')) {
              $slider.slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                variableWidth: false,
                arrows: true,
                prevArrow: jQuery(`.${tabClass} .mobile-controllers .left-arrow`),
                nextArrow: jQuery(`.${tabClass} .mobile-controllers .right-arrow`),
              });
              console.log(`Initialized mobile slick slider for ${tabClass}`);
            }
          } catch (e) {
            console.error(`Error initializing mobile slider for ${tabClass}:`, e);
          }
        }
      });

      // Handle mobile Nice Select changes
      jQuery('.nice-select ul li')
        .off('click.winesmobile')
        .on('click.winesmobile', function (e) {
          let value = jQuery(this).attr('data-value');
          let $mobileLink = jQuery('.mobile-browse-links .mobile-link');

          // Find the corresponding wine offering and trigger its click
          let offeringClass = '';
          switch (value) {
            case '1':
              offeringClass = 'napa-valley';
              break;
            case '2':
              offeringClass = 'limited-release';
              break;
            case '3':
              offeringClass = 'generations-family';
              break;
            case '4':
              offeringClass = 'vintage-selection';
              break;
          }

          if (offeringClass) {
            jQuery(`.wine-offering.${offeringClass}`).trigger('click');
            $mobileLink.attr('data-filter', offeringClass);
          }

          // Close dropdown
          setTimeout(() => {
            jQuery('.nice-select').removeClass('open');
          }, 150);
        });
    } else {
      // Desktop initialization (existing code)
      Object.keys(sliderMap).forEach(function (tabClass) {
        const $sliderElement = sliderMap[tabClass];
        if (!$sliderElement.find('.c7-product-collection').length) {
          try {
            if (!$sliderElement.hasClass('slick-initialized')) {
              $sliderElement.slick({
                slidesToShow: 3,
                slidesToScroll: 1,
                variableWidth: true,
                arrows: true,
                prevArrow: jQuery(`.${tabClass} .left-arrow`),
                nextArrow: jQuery(`.${tabClass} .right-arrow`),
              });
              console.log(`Initialized desktop slick slider for ${tabClass}`);
            }
          } catch (e) {
            console.error(`Error initializing desktop slider for ${tabClass}:`, e);
          }
        }
      });
    }

    // Now set up event handlers for each slider dynamically
    Object.keys(sliderMap).forEach(function (tabClass) {
      const $slider = sliderMap[tabClass];

      $slider.on('init reInit afterChange', function (event, slick, currentSlide, nextSlide) {
        var i = (currentSlide ? currentSlide : 0) + 1;
        $wineTotalCount = slick.slideCount;
        $wineCurrentCount = i;

        // Find the progress bar in the content with this tab class
        const $progressBar = jQuery(`.wine-content.${tabClass} .progress-bar`);
        if ($progressBar.length) {
          try {
            // Calculate segment width based on total slides
            const segmentWidth = (100 / slick.slideCount).toFixed(4);

            // Calculate position based on current slide (similar to CustomExp.js)
            const position = i * segmentWidth + '%';

            // Set the slider handle position directly using CustomExp.js approach
            jQuery(`.wine-content.${tabClass} .ui-slider-handle`).css({
              width: segmentWidth + '%',
              'margin-left': '-' + segmentWidth + '%',
              left: position,
            });

            console.log(
              `Updated slider for ${tabClass}: slide ${i}/${slick.slideCount}, position: ${position}`
            );
          } catch (e) {
            console.error(`Error updating progress for ${tabClass}:`, e);
          }
        }
      });
    });

    // Initialize all slick sliders if they exist
    const initializeSlickSlider = ($element, options = {}) => {
      if ($element && $element.length) {
        // Don't try to initialize if this is a C7 element or container with C7 elements
        if (
          $element.hasClass('c7-product-collection__product-list') ||
          $element.find('.c7-product-collection').length ||
          $element.closest('.wine-content').find('.c7-product-collection').length
        ) {
          console.log(
            `Skipping slick initialization for element with C7: ${$element.attr('class')}`
          );
          return;
        }

        try {
          if (!$element.hasClass('slick-initialized')) {
            $element.slick({
              dots: false,
              infinite: true,
              autoplay: false,
              slidesToShow: 3,
              slidesToScroll: 1,
              centerMode: false,
              variableWidth: false,
              prevArrow: $element.closest('.wine-content').find('.left-arrow'),
              nextArrow: $element.closest('.wine-content').find('.right-arrow'),
              responsive: [
                {
                  breakpoint: 767,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    centerMode: true,
                    variableWidth: true,
                  },
                },
              ],
              ...options,
            });
            console.log(`Initialized slick slider for ${$element.attr('class')}`);
          } else {
            console.log(`Slick already initialized for ${$element.attr('class')}`);
          }
        } catch (e) {
          console.error(`Error initializing slick slider for ${$element.attr('class')}:`, e);
        }
      }
    };

    // Initialize all sliders from our map
    Object.keys(sliderMap).forEach(function (tabClass) {
      const $sliderElement = sliderMap[tabClass];
      // Skip any elements that contain or are contained by C7 elements
      if (
        !$sliderElement.find('.c7-product-collection').length &&
        !$sliderElement.closest('.wine-content').find('.c7-product-collection').length
      ) {
        initializeSlickSlider(sliderMap[tabClass]);
      } else {
        console.log(`Skipping slick for ${tabClass} due to C7 presence`);
      }
    });

    // Add click handlers for arrow navigation to update slider position
    jQuery('.wine-content .left-arrow, .wine-content .right-arrow').on('click', function () {
      // Find the containing tab class
      const $content = jQuery(this).closest('.wine-content');
      let tabClass = '';
      $content
        .attr('class')
        .split(' ')
        .forEach(function (cls) {
          if (cls !== 'wine-content' && cls !== 'active') {
            tabClass = cls;
          }
        });

      if (tabClass) {
        // Allow time for slick to update its state
        setTimeout(() => {
          // Check if this is a C7 slider
          const hasC7 = $content.find('.c7-product-collection').length > 0;

          if (hasC7) {
            // For C7 sliders, use the C7 product list
            const $slider = $content.find('.c7-product-collection__product-list');
            if ($slider.length && $slider.hasClass('slick-initialized')) {
              const slick = $slider.slick('getSlick');
              const currentSlide = slick.currentSlide;
              const totalSlides = slick.slideCount;

              // Calculate segment width
              const segmentWidth = (100 / totalSlides).toFixed(4);

              // Calculate position based on current slide (add 1 because slides are 0-indexed)
              const position = (currentSlide + 1) * segmentWidth + '%';

              // Update just the width and margin
              $content.find('.ui-slider-handle').css({
                width: segmentWidth + '%',
                'margin-left': '-' + segmentWidth + '%',
              });

              // Animate the position change with GSAP
              gsap.to($content.find('.ui-slider-handle'), {
                duration: 0.3,
                ease: 'power2.out',
                left: position,
              });

              console.log(
                `C7 arrow navigation: slide ${
                  currentSlide + 1
                }/${totalSlides}, position: ${position}`
              );
            }
          } else {
            // For legacy sliders, use the original approach with GSAP
            const $slider = $content.find('.slider-content');
            if ($slider.length && $slider.hasClass('slick-initialized')) {
              const slick = $slider.slick('getSlick');
              const currentSlide = slick.currentSlide;
              const totalSlides = slick.slideCount;

              // Calculate segment width
              const segmentWidth = (100 / totalSlides).toFixed(4);

              // Calculate position based on current slide
              const position = (currentSlide + 1) * segmentWidth + '%';

              // Update just the width and margin
              $content.find('.ui-slider-handle').css({
                width: segmentWidth + '%',
                'margin-left': '-' + segmentWidth + '%',
              });

              // Animate the position change with GSAP
              gsap.to($content.find('.ui-slider-handle'), {
                duration: 0.3,
                ease: 'power2.out',
                left: position,
              });

              console.log(
                `Legacy arrow navigation: slide ${
                  currentSlide + 1
                }/${totalSlides}, position: ${position}`
              );
            }
          }
        }, 50);
      }
    });

    // Set up a mutation observer to detect Commerce7 elements being added
    this.setupC7Observer();
  },

  setupC7Observer: function () {
    // Only set up the observer if we don't already have one
    if (this._c7Observer) {
      this._c7Observer.disconnect();
    }

    // Create a new mutation observer
    this._c7Observer = new MutationObserver((mutations) => {
      let c7ElementAdded = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              // Check for C7 elements
              if (
                node.classList &&
                (node.classList.contains('c7-product-collection') ||
                  node.classList.contains('c7-product-collection__product-list') ||
                  node.querySelector('.c7-product-collection__product-list'))
              ) {
                c7ElementAdded = true;
              }
            }
          });
        }
      });

      if (c7ElementAdded) {
        console.log('C7 elements detected in Wines.js, updating active tab...');
        // Trigger a click on the active offering to reinitialize the view
        setTimeout(() => {
          const $activeOffering = jQuery('.wine-offering.active');
          if ($activeOffering.length) {
            $activeOffering.trigger('click');

            // Setup C7 specific slider event handlers
            this.setupC7SliderEvents();
          }
        }, 500);
      }
    });

    // Start observing
    this._c7Observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    console.log('Wines.js C7 observer set up');

    // Initial setup for any C7 sliders already in the DOM
    this.setupC7SliderEvents();
  },

  setupC7SliderEvents: function () {
    // Target C7 sliders specifically
    const $c7Tabs = jQuery('.wine-content').filter(function () {
      return jQuery(this).find('.c7-product-collection').length > 0;
    });

    if ($c7Tabs.length === 0) {
      return;
    }

    console.log('Setting up C7 slider events for', $c7Tabs.length, 'tabs');

    $c7Tabs.each(function () {
      const $tab = jQuery(this);
      const $progressBar = $tab.find('.progress-bar');
      const $c7Slider = $tab.find('.c7-product-collection__product-list.slick-initialized');

      if (!$progressBar.length || !$c7Slider.length) {
        return;
      }

      // Get the actual slick instance
      const slickInstance = $c7Slider.slick('getSlick');
      if (!slickInstance) {
        return;
      }

      // Get the total number of real slides (excluding clones)
      const totalSlides = slickInstance.slideCount;
      console.log(`C7 slider found with ${totalSlides} slides`);

      // Calculate segment width based on total slides
      const segmentWidth = (100 / totalSlides).toFixed(4);

      // Update the UI slider handle dimensions and initial position
      $tab.find('.ui-slider-handle').css({
        width: segmentWidth + '%',
        'margin-left': '-' + segmentWidth + '%',
      });

      // Use GSAP to set initial position with animation
      gsap.set($tab.find('.ui-slider-handle'), {
        left: (slickInstance.currentSlide + 1) * segmentWidth + '%',
      });

      // Remove any previous event handlers
      $c7Slider.off('afterChange.c7wines');

      // Add event handler for slick afterChange event
      $c7Slider.on('afterChange.c7wines', function (event, slick, currentSlide) {
        const slideIndex = currentSlide + 1;
        const position = slideIndex * segmentWidth + '%';

        // Update just the width and margin (not animated properties)
        $tab.find('.ui-slider-handle').css({
          width: segmentWidth + '%',
          'margin-left': '-' + segmentWidth + '%',
        });

        // Use GSAP to animate the position change
        gsap.to($tab.find('.ui-slider-handle'), {
          duration: 0.3,
          ease: 'power2.out',
          left: position,
          onComplete: function () {
            // For sliding back to start when reaching the end
            if (slideIndex >= totalSlides && slickInstance.options.infinite) {
              console.log('Reached the end, smooth transition to first slide next');
            }
          },
        });

        console.log(`C7 slider changed: slide ${slideIndex}/${totalSlides}, position: ${position}`);
      });

      // Add specific handlers for the arrow clicks
      const $leftArrow = $tab.find('.left-arrow');
      const $rightArrow = $tab.find('.right-arrow');

      // Remove any previous handlers
      $leftArrow.off('click.c7wines');
      $rightArrow.off('click.c7wines');

      // Add click handlers for arrows
      $leftArrow.on('click.c7wines', function () {
        $c7Slider.slick('slickPrev');
      });

      $rightArrow.on('click.c7wines', function () {
        $c7Slider.slick('slickNext');
      });
    });
  },

  WinesVineyardSlider: function () {
    // Check if elements exist before initializing
    if (!jQuery('.vineyard-spotlights').length) {
      console.log('No vineyard spotlights found, skipping WinesVineyardSlider');
      return;
    }

    console.log('Initializing WinesVineyardSlider');

    let $vin = jQuery('.vineyard-spotlights .vineyards .vineyard-single');
    let $vinContainer = jQuery('.vineyard-spotlights .vineyards');
    let $vinBG = jQuery('.vineyard-bg__container .vineyard-bg');
    let $vinBGContainer = jQuery('.vineyard-bg__container');

    // Ensure we have all required elements
    if (!$vin.length || !$vinContainer.length || !$vinBG.length || !$vinBGContainer.length) {
      console.log('Missing required elements for vineyard slider');
      return;
    }

    // Reset initial state
    $vin.removeClass('active').first().addClass('active');
    $vinBG.removeClass('active').first().addClass('active');

    //Text change
    jQuery('.vineyard-bg-position .vineyard-spotlights .number .total-number').text(
      $vin.length.toString()
    );
    jQuery('.vineyard-bg-position .vineyard-spotlights .number .current-number').text('1');

    function ImageChange() {
      const $activeBG = jQuery('.vineyard-bg__container .active');
      if ($activeBG.is(':last-child')) {
        $vinBG.removeClass('active');
        $vinBG.first().addClass('active');
        jQuery('.vineyard-bg-position .vineyard-spotlights .number .current-number').text('1');
      } else {
        const $nextBG = $activeBG.next();
        $vinBG.removeClass('active');
        $nextBG.addClass('active');
        jQuery('.vineyard-bg-position .vineyard-spotlights .number .current-number').text(
          ($nextBG.index() + 1).toString()
        );
      }
    }

    // Remove any existing click handlers to prevent duplicates
    jQuery('.vineyard__controller .next, .vineyard__controller .prev').off('click');

    jQuery('.vineyard__controller .next').on('click', function () {
      const $active = jQuery('.vineyard-spotlights .vineyards .active');
      if ($active.is(':last-child')) {
        $vin.removeClass('active');
        $vin.first().addClass('active');
      } else {
        $vin.removeClass('active');
        $active.next().addClass('active');
      }
      ImageChange();
    });

    jQuery('.vineyard__controller .prev').on('click', function () {
      const $active = jQuery('.vineyard-spotlights .vineyards .active');
      if ($active.is(':first-child')) {
        $vin.removeClass('active');
        $vin.last().addClass('active');
      } else {
        $vin.removeClass('active');
        $active.prev().addClass('active');
      }
      ImageChange();
    });

    console.log('WinesVineyardSlider initialized successfully');
  },

  scrollAnim: function () {
    // Intro animation section
    const $introSubtitle = jQuery('.wine-intro__wrapper .p-content .subtitle');
    const $introTitle = jQuery('.wine-intro__wrapper .p-content .title');
    const $introParagraph = jQuery('.wine-intro__wrapper .p-content .paragraph');
    const $introGoldLink = jQuery('.wine-intro__wrapper .p-content .gold-link');
    const $introImageContent = jQuery('.wine-intro__wrapper .image-content');
    const $introWrapper = jQuery('.wine-intro__wrapper');

    if ($introWrapper.length) {
      let introTL = new TimelineMax({ paused: true });

      // Only add animations for elements that exist
      if ($introSubtitle.length) {
        introTL.fromTo(
          $introSubtitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          0
        );
      }

      if ($introTitle.length) {
        introTL.fromTo(
          $introTitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($introParagraph.length) {
        introTL.fromTo(
          $introParagraph,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($introGoldLink.length) {
        introTL.fromTo(
          $introGoldLink,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($introImageContent.length) {
        introTL.fromTo(
          $introImageContent,
          0.8,
          { autoAlpha: 0, x: 18, ease: Power2.easeIn },
          { autoAlpha: 1, x: 0, ease: Power2.easeInOut },
          0
        );
      }

      let introSectionHeight = $introWrapper.outerHeight(true);

      let introScene = new ScrollMagic.Scene({
        triggerElement: '.wine-intro__wrapper',
        duration: introSectionHeight,
        triggerHook: 0.5,
        offset: 0,
      })
        .on('enter', function (event) {
          introTL.play();
        })
        .on('leave', function (event) {});

      this.wineController.addScene(introScene);
    }

    // Discover our wines section
    const $discoverWrapper = jQuery('.discover-our-wines__wrapper');
    const $discoverSubtitle = jQuery(
      '.discover-our-wines__wrapper .discover-our-wines__container .subtitle'
    );
    const $discoverTitle = jQuery(
      '.discover-our-wines__wrapper .discover-our-wines__container .title'
    );
    const $discoverParagraph = jQuery(
      '.discover-our-wines__wrapper .discover-our-wines__container .paragraph'
    );
    const $discoverGoldLink = jQuery(
      '.discover-our-wines__wrapper .discover-our-wines__container .gold-link'
    );

    if ($discoverWrapper.length) {
      let wineSliderTL = new TimelineMax({ paused: true });

      if ($discoverSubtitle.length) {
        wineSliderTL.fromTo(
          $discoverSubtitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          0
        );
      }

      if ($discoverTitle.length) {
        wineSliderTL.fromTo(
          $discoverTitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($discoverParagraph.length) {
        wineSliderTL.fromTo(
          $discoverParagraph,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($discoverGoldLink.length) {
        wineSliderTL.fromTo(
          $discoverGoldLink,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      let discoverSectionHeight = $discoverWrapper.outerHeight(true);

      let discoveryScene = new ScrollMagic.Scene({
        triggerElement: '.discover-our-wines__wrapper',
        duration: discoverSectionHeight,
        triggerHook: 0.5,
        offset: 0,
      })
        .on('enter', function (event) {
          wineSliderTL.play();
        })
        .on('leave', function (event) {});

      this.wineController.addScene(discoveryScene);
    }

    // Vineyard spotlight section
    const $vineyardWrapper = jQuery('.vineyard-spotlight__wrapper');
    const $vineyardSubtitle = jQuery('.vineyard-spotlight__wrapper .subtitle');
    const $vineyardTitle = jQuery('.vineyard-spotlight__wrapper .title');
    const $vineyardParagraph = jQuery('.vineyard-spotlight__wrapper .paragraph');

    if ($vineyardWrapper.length) {
      let spotlightTL = new TimelineMax({ paused: true });

      if ($vineyardSubtitle.length) {
        spotlightTL.fromTo(
          $vineyardSubtitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          0
        );
      }

      if ($vineyardTitle.length) {
        spotlightTL.fromTo(
          $vineyardTitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      if ($vineyardParagraph.length) {
        spotlightTL.fromTo(
          $vineyardParagraph,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=.25'
        );
      }

      let spotlightSectionHeight = $vineyardWrapper.outerHeight(true);

      let spotlightScene = new ScrollMagic.Scene({
        triggerElement: '.vineyard-spotlight__wrapper',
        duration: spotlightSectionHeight,
        triggerHook: 0.5,
        offset: 0,
      })
        .on('enter', function (event) {
          spotlightTL.play();
        })
        .on('leave', function (event) {});

      this.wineController.addScene(spotlightScene);
    }

    // Stacy section
    const $stacyContainer = jQuery('.stacy-container');
    const $stacyImg = jQuery('.stacy-container .stacy-img');
    const $stacyTitle = jQuery('.stacy-container .stacy-content .title');
    const $stacyQuoteName = jQuery('.stacy-container .stacy-content .quote-name');
    const $stacyParagraph = jQuery('.stacy-container .stacy-content .paragraph');

    if ($stacyContainer.length) {
      let stacyTL = new TimelineMax({ paused: true });

      if ($stacyImg.length) {
        stacyTL.fromTo(
          $stacyImg,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          0
        );
      }

      if ($stacyTitle.length) {
        stacyTL.fromTo(
          $stacyTitle,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=0'
        );
      }

      if ($stacyQuoteName.length) {
        stacyTL.fromTo(
          $stacyQuoteName,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=0'
        );
      }

      if ($stacyParagraph.length) {
        stacyTL.fromTo(
          $stacyParagraph,
          0.5,
          { y: 8, autoAlpha: 0, ease: Power2.easeIn },
          { y: 0, autoAlpha: 1, ease: Power2.easeIn },
          '-=0'
        );
      }

      let stacySecHeight = $stacyContainer.outerHeight(true);

      let stacyScene = new ScrollMagic.Scene({
        triggerElement: '.stacy-container',
        duration: stacySecHeight,
        triggerHook: 0.5,
        offset: 0,
      })
        .on('enter', function (event) {
          stacyTL.play();
        })
        .on('leave', function (event) {});

      this.wineController.addScene(stacyScene);
    }

    // Finder section
    const $winesLocatorWrapper = jQuery('.wines-locator__wrapper');
    const $finderIntroTitle = jQuery('.finder-intro .title');

    if ($winesLocatorWrapper.length && $finderIntroTitle.length) {
      let grapposTL = new TimelineMax({ paused: true });

      grapposTL.fromTo(
        $finderIntroTitle,
        0.5,
        { y: 8, autoAlpha: 0, ease: Power2.easeIn },
        { y: 0, autoAlpha: 1, ease: Power2.easeIn },
        0
      );

      let finderSecHeight = $winesLocatorWrapper.outerHeight(true);

      let finderScene = new ScrollMagic.Scene({
        triggerElement: '.wines-locator__wrapper',
        duration: finderSecHeight,
        triggerHook: 0.5,
        offset: 0,
      })
        .on('enter', function (event) {
          grapposTL.play();
        })
        .on('leave', function (event) {});

      this.wineController.addScene(finderScene);
    }
  },

  locatorOnLoad: function () {
    console.log('Initializing wine locator (VIP iframe)');
    const rootElement = document.getElementById('vip_placeholder');

    if (!rootElement) {
      console.log('No vip_placeholder element found, skipping locator initialization');
      return;
    }

    try {
      // Create the iframe element if it doesn't already exist
      if (!this.vipIframe) {
        this.vipIframe = document.createElement('iframe');
        this.vipIframe.src =
          'https://www.vtinfo.com/PF/product_finder.asp?custID=02E&theme=bs-paper&category3=CHARLES KRUG';
        this.vipIframe.style.display = 'none';
        this.vipIframe.id = 'vip_locator';
        this.vipIframe.width = '650';
        this.vipIframe.height = '600';
        this.vipIframe.frameBorder = '0';
        this.vipIframe.scrolling = 'no';
        this.vipIframe.allow = 'geolocation';

        // Append the iframe to the root element
        rootElement.appendChild(this.vipIframe);
        console.log('VIP locator iframe created and appended to the DOM');

        // Show the iframe after a short delay
        setTimeout(() => {
          const iframe = document.getElementById('vip_locator');
          if (iframe) {
            iframe.style.display = 'block';
            console.log('VIP locator iframe display set to block');
          }
        }, 1000);
      }
    } catch (e) {
      console.error('Error initializing wine locator:', e);
    }
  },

  initScrollAnimations: function () {
    console.log('Wines: initializing scroll animations');

    // Make sure we have a controller
    if (!this.wineController) {
      console.log('Creating new ScrollMagic controller for Wines');
      this.wineController = new ScrollMagic.Controller();
    }

    // Run the scroll animations
    try {
      this.scrollAnim();
      console.log('Wines ScrollMagic animations initialized successfully');
    } catch (e) {
      console.error('Error initializing Wines ScrollMagic animations:', e);
    }
  },

  init: function () {
    console.log('Wines.init called');

    // Initialize components that are guaranteed to be defined
    this.WineSliderOffering();
    this.WinesACFSlider();
    this.WinesVineyardSlider();

    // Initialize the wine locator iframe
    this.locatorOnLoad();

    // Initialize optional components - check if they exist first
    if (typeof this.SelectChange === 'function') {
      console.log('Initializing SelectChange');
      this.SelectChange();
    } else {
      console.log('SelectChange function not found, skipping');
    }

    if (typeof this.ShopFilter === 'function') {
      console.log('Initializing ShopFilter');
      this.ShopFilter();
    } else {
      console.log('ShopFilter function not found, skipping');
    }

    // Initialize other required components with existence checks
    if (jQuery('#wineOfferings-control').length && typeof this.NiceSelect === 'function') {
      console.log('Initializing NiceSelect');
      this.NiceSelect();
    }

    if (jQuery('.ecp-wrapper').length && typeof this.eCellarHook === 'function') {
      console.log('Initializing eCellarHook');
      this.eCellarHook();
    }

    // Initialize scroll animations
    this.initScrollAnimations();
  },
};

// Define Barba.js hooks with modern API
const winesHooks = {
  beforeEnter(data) {
    console.log('Wines: beforeEnter', data);

    // Check if the controller exists and destroy it to prevent duplicates
    if (Wines.wineController) {
      try {
        Wines.wineController.destroy(true);
        Wines.wineController = null;
        console.log('Destroyed previous Wines ScrollMagic controller');
      } catch (e) {
        console.error('Error destroying Wines controller:', e);
      }
    }

    // Reset the vipIframe to null so it will be reinitialized
    Wines.vipIframe = null;
  },

  enter(data) {
    console.log('Wines: enter', data);

    // Wait for DOM to be fully ready before initializing
    setTimeout(() => {
      Wines.init();
      console.log('Wines.init completed');
    }, 100);
  },

  afterEnter(data) {
    console.log('Wines: afterEnter', data);

    // Wait for styles to be applied and DOM to be stable
    setTimeout(() => {
      if (!App.isMobile()) {
        Wines.initScrollAnimations();
      } else {
        console.log('Mobile device detected, skipping scroll animations');
      }

      // Re-initialize the wine locator if needed
      if (document.getElementById('vip_placeholder') && !document.getElementById('vip_locator')) {
        console.log('Reinitializing wine locator after page transition');
        Wines.locatorOnLoad();
      }
    }, 500);
  },

  leave(data) {
    console.log('Wines: leave', data);

    if (Wines.wineController) {
      try {
        Wines.wineController.destroy(true);
        Wines.wineController = null;
        console.log('Wines controller destroyed during leave');
      } catch (e) {
        console.error('Error destroying Wines controller:', e);
      }
    }
  },
};

const wineFinderHooks = {
  beforeEnter(data) {
    console.log('WineFinder: beforeEnter', data);
  },

  enter(data) {
    console.log('WineFinder: enter', data);
    // Initialize any wine finder specific functionality here
    if (document.getElementById('vip_placeholder')) {
      Wines.locatorOnLoad();
    }
  },

  afterEnter(data) {
    console.log('WineFinder: afterEnter', data);
  },

  leave(data) {
    console.log('WineFinder: leave', data);
  },
};

// Make hooks available for Barba.js
Wines.barbaHooks = {
  wines: winesHooks,
  wineFinder: wineFinderHooks,
};

export default Wines;
